import { useMemo } from "react";
import { Link } from "wouter";
import { useListContracts, getListContractsQueryKey } from "@workspace/api-client-react";
import type { ContractListItem } from "@workspace/api-client-react";
import { binContractsForNegotiations } from "./negotiations.filter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { LifecycleStateBadge } from "@/components/LifecycleStateBadge";
import {
  Send,
  ArrowLeftRight,
  Inbox,
  ChevronRight,
  AlertCircle,
  FileText,
} from "lucide-react";
import { format } from "date-fns";

export default function Negotiations() {
  const { data, isLoading } = useListContracts(undefined, {
    query: { queryKey: getListContractsQueryKey() },
  });
  const contracts: ContractListItem[] = useMemo(
    () => (Array.isArray(data) ? (data as ContractListItem[]) : []),
    [data],
  );

  // Bin contracts into negotiation buckets. The pure binning logic lives in
  // `./negotiations.filter` so it can be unit-tested independently of React.
  // See that file for the full rationale on why we look at openIssuesCount in
  // addition to lifecycleState (TL;DR: contracts in internal_review /
  // legal_review can have active negotiation rounds — by design — and the
  // old filter dropped them from the dashboard).
  const grouped = useMemo(
    () => binContractsForNegotiations(contracts),
    [contracts],
  );

  const activeCount = grouped.out.length + grouped.counter.length;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h1 className="flex items-center gap-3">
          Negotiations
          {activeCount > 0 && (
            <Badge variant="secondary" className="bg-orange-100 text-orange-800 border-orange-200">
              {activeCount} active
            </Badge>
          )}
        </h1>
        <p>Contracts currently in the back-and-forth phase between you and the coach or representation.</p>
      </div>

      {/* Counters waiting on you — highest priority */}
      <NegotiationGroup
        title="Counters waiting on you"
        description="The other side has responded — review and decide your next move."
        accent="amber"
        icon={ArrowLeftRight}
        contracts={grouped.counter}
        emptyText="No counter-proposals waiting for review."
      />

      {/* Out with counterparty */}
      <NegotiationGroup
        title="Out for negotiation"
        description="Sent externally — waiting on the coach or representation to respond."
        accent="orange"
        icon={Send}
        contracts={grouped.out}
        emptyText="Nothing currently out for negotiation."
      />

      {/* Coming soon — drafts in review that will likely go out */}
      {grouped.upstream.length > 0 && (
        <NegotiationGroup
          title="Almost ready to send"
          description="In internal or legal review — likely the next round of negotiations."
          accent="muted"
          icon={FileText}
          contracts={grouped.upstream}
          emptyText="Nothing is staged."
          collapsedHint
        />
      )}

      {activeCount === 0 && grouped.upstream.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto">
              <Inbox className="h-6 w-6 text-muted-foreground" />
            </div>
            <div>
              <h3 className="font-semibold text-base mb-1">No negotiations in flight</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                When you send a contract to a coach or their representation, it'll show up here and travel through approval and signing.
              </p>
            </div>
            <Button asChild variant="outline" size="sm">
              <Link href="/contracts">View all contracts</Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface NegotiationGroupProps {
  title: string;
  description: string;
  accent: "amber" | "orange" | "muted";
  icon: React.ComponentType<{ className?: string }>;
  contracts: ContractListItem[];
  emptyText: string;
  collapsedHint?: boolean;
}

function NegotiationGroup({
  title,
  description,
  accent,
  icon: Icon,
  contracts,
  emptyText,
  collapsedHint,
}: NegotiationGroupProps) {
  const items = contracts ?? [];
  const accentClasses = {
    amber: "border-amber-200 bg-amber-50/50",
    orange: "border-orange-200 bg-orange-50/50",
    muted: "border-border bg-muted/20",
  }[accent];
  const accentText = {
    amber: "text-amber-700",
    orange: "text-orange-700",
    muted: "text-muted-foreground",
  }[accent];

  return (
    <Card className={accentClasses}>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Icon className={`h-4 w-4 ${accentText}`} />
              {title}
              <Badge variant="outline" className="ml-1 text-xs">
                {items.length}
              </Badge>
            </CardTitle>
            <CardDescription className="mt-1">{description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className={`text-sm italic ${collapsedHint ? "text-muted-foreground/70" : accentText}`}>{emptyText}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((c) => {
              const lifecycleState = (c as { lifecycleState?: string }).lifecycleState ?? "draft";
              const openIssues = (c as { openIssuesCount?: number }).openIssuesCount ?? 0;
              return (
                <Link key={c.id} href={`/contracts/${c.id}#negotiation`}>
                  <Card className="hover:shadow-md hover:border-primary/40 transition-all cursor-pointer h-full bg-background">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold text-sm truncate flex items-center gap-1.5">
                            <span className="truncate">{c.coachName ?? "Untitled contract"}</span>
                            <span
                              className="font-mono text-[10px] text-muted-foreground bg-muted/60 border rounded px-1 py-px shrink-0"
                              title={`Contract ID #${c.id}`}
                            >
                              #{c.id}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {c.sport ?? "—"} · {(c.contractType ?? "").replace("_", " ")}
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <LifecycleStateBadge state={lifecycleState} size="sm" />
                        {openIssues > 0 && (
                          <Badge
                            variant="outline"
                            className="text-[10px] bg-amber-50 text-amber-800 border-amber-200 flex items-center gap-1"
                          >
                            <AlertCircle className="h-2.5 w-2.5" />
                            {openIssues} open issue{openIssues === 1 ? "" : "s"}
                          </Badge>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground pt-1 border-t border-border/50">
                        Updated {(() => {
                          const updatedAt = (c as unknown as { updatedAt?: string }).updatedAt;
                          return updatedAt ? format(new Date(updatedAt), "MMM d, yyyy") : "—";
                        })()}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
